/**
 * Which client company THIS BROWSER TAB is working in, when the person is a partner user working inside a client company.
 *
 * What is stored, and where: only the client's relationship id, in `sessionStorage`, which is private to one tab. The delegated access token itself is an httpOnly cookie that page script cannot read, and
 * nothing here (or anywhere in the web app) puts a token in `localStorage` or `sessionStorage`. The id is sent on every module request as `X-Epn-Client-Context`; a module refuses a request whose token is for a
 * different client than this header, so when another tab changes the client this tab's next call is refused (409) instead of silently working in the wrong company. The server decides everything; this is a label.
 */
export const CLIENT_CONTEXT_HEADER = 'X-Epn-Client-Context';

const STORAGE_KEY = 'epn.clientContext';

export function getClientContext(): string | null {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

const contextListeners = new Set<() => void>();

/** For `useSyncExternalStore`: the screen re-reads this tab's client whenever it changes. */
export function subscribeClientContext(listener: () => void): () => void {
  contextListeners.add(listener);
  return () => {
    contextListeners.delete(listener);
  };
}

export function setClientContext(relationshipId: string | null): void {
  try {
    if (relationshipId) window.sessionStorage.setItem(STORAGE_KEY, relationshipId);
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage can be blocked (private window): the context then lives only until reload, and the header is simply not sent.
  }

  for (const listener of [...contextListeners]) listener();
}

/** What a module said about the delegated session, from the stable `code` of its refusal. */
export type ClientContextProblem = 'mismatch' | 'invalid' | 'unavailable';

const PROBLEM_BY_CODE: Record<string, ClientContextProblem> = {
  CLIENT_CONTEXT_MISMATCH: 'mismatch',
  PARTNER_DELEGATION_INVALID: 'invalid',
  PARTNER_DELEGATION_UNAVAILABLE: 'unavailable',
};

export function clientContextProblemFromCode(code: string | null | undefined): ClientContextProblem | null {
  return code ? (PROBLEM_BY_CODE[code] ?? null) : null;
}

type Listener = (problem: ClientContextProblem) => void;
const listeners = new Set<Listener>();

export function onClientContextProblem(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function emitClientContextProblem(problem: ClientContextProblem): void {
  for (const listener of [...listeners]) listener(problem);
}
